import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import BoltRounded from "@mui/icons-material/BoltRounded";

import type { Bot } from "@/data/mock-data";
import { formatUSD, formatNumericInput } from "@/lib/formatters";
import { TRADE_TIMING_OPTIONS, DEFAULT_TRADE_TIMING } from "@/lib/trade-timing";
import type { TradeTiming } from "@/lib/trade-timing";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Types ----------------------------------------------------------------

export type TradeMode = "buy" | "sell";

interface BotTradeSheetProps {
  open: boolean;
  /** "buy" adds capital to the bot; "sell" pulls it out. */
  mode: TradeMode;
  bot: Bot;
  /** Owning account display name, shown on the Account row. */
  accountName: string;
  onClose: () => void;
  /**
   * Called once the user confirms the order. The message is surfaced
   * by the parent as a toast — there's no live order router in this
   * build, so the trade is acknowledged optimistically.
   */
  onSubmitted: (message: string) => void;
}

/** Quick-add buttons on the Buy step — tap to bump the amount. */
const BUY_QUICK_ADDS = [100, 1000, 10000] as const;

/**
 * When a sell covers ≥ this share of the position we recommend a full
 * liquidation instead — selling the last few percent piecemeal leaves
 * dust behind and the rebalance maths get noisy. Mirrors the trading-
 * app convention surfaced in the reference screenshots.
 */
const LIQUIDATE_THRESHOLD = 0.9;

// ---- Component ------------------------------------------------------------

/**
 * Buy / Sell bottom sheet for an owned bot (Phase 6, Sergey
 * 2026-05-21). A two-step flow inside one SwipeableDrawer:
 *
 *   input   — amount entry. Buy: quick-add chips + Account + Buying
 *             Power rows. Sell: Available-to-sell row + Sell All. A
 *             sell of ≥ 90% flips the primary button to "Liquidate".
 *   confirm — Amount / Account / Trades-at summary with Confirm +
 *             Modify.
 *
 * The amount field uses `inputMode="decimal"` so phones surface the
 * numeric keypad without us hand-rolling one.
 */
export function BotTradeSheet({
  open,
  mode,
  bot,
  accountName,
  onClose,
  onSubmitted
}: BotTradeSheetProps) {
  const [step, setStep] = useState<"input" | "confirm">("input");
  const [amountStr, setAmountStr] = useState("");
  // When the order is sent to market — see TRADE_TIMING_OPTIONS.
  const [timing, setTiming] = useState<TradeTiming>(DEFAULT_TRADE_TIMING);

  // Reset to a clean input step every time the sheet opens — the user
  // should never land on a stale confirm screen or a pre-filled amount
  // from a previous trade.
  useEffect(() => {
    if (open) {
      setStep("input");
      setAmountStr("");
      setTiming(DEFAULT_TRADE_TIMING);
    }
  }, [open, mode, bot.id]);

  const amount = useMemo(() => {
    const n = parseFloat(amountStr);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [amountStr]);

  const available = bot.currentValue;
  const sellRatio = mode === "sell" && available > 0 ? amount / available : 0;
  const isLiquidation = mode === "sell" && sellRatio >= LIQUIDATE_THRESHOLD;

  const isBuy = mode === "buy";
  const canProceed = amount > 0 && (isBuy || amount <= available + 0.005);

  function bumpAmount(delta: number) {
    const next = (parseFloat(amountStr) || 0) + delta;
    setAmountStr(String(Math.round(next * 100) / 100));
  }

  function handleConfirm() {
    // "Now" reads naturally without a trailing clause; the scheduled
    // windows get an explicit "— <when>" suffix.
    const when = timing === "Now" ? "" : ` — ${timing.toLowerCase()}`;
    const msg = isBuy
      ? `Buy order submitted — ${formatUSD(amount)} into ${bot.name}${when}.`
      : isLiquidation
        ? `Liquidation submitted — ${bot.name} will move fully to cash${when}.`
        : `Sell order submitted — ${formatUSD(amount)} from ${bot.name}${when}.`;
    onSubmitted(msg);
    onClose();
  }

  const title =
    step === "confirm" ? "Confirm" : isBuy ? "Buy" : "Sell";

  // Primary button label on the input step.
  const inputCta = isBuy ? "Next" : isLiquidation ? "Liquidate" : "Sell";

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      // Above the bot detail drawer it sits on top of.
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Close (×) */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(t: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(t.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(t.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      {/* Scrollable content — the amount input lives here; the CTA is
       *  pinned in the footer below so it stays above the mobile
       *  numeric keyboard (Sergey 2026-05-21). */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pt: 1, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, pr: 5 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.25, mb: 2 }}
        >
          {bot.name}
        </Typography>

        {step === "input" ? (
          <>
            {/* Buy: quick-add chips */}
            {isBuy && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {BUY_QUICK_ADDS.map((v) => (
                  <Button
                    key={v}
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => bumpAmount(v)}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  >
                    +{formatUSD(v, { maximumFractionDigits: 0 })}
                  </Button>
                ))}
              </Stack>
            )}

            {/* Amount input — big, $-prefixed, thousands-separated as
             *  the user types. inputMode decimal → mobile numeric keypad. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                py: 1
              }}
            >
              <Typography
                sx={(t) => ({
                  fontSize: 40,
                  fontWeight: 700,
                  color:
                    amount > 0 ? t.palette.success.main : t.palette.text.disabled
                })}
              >
                $
              </Typography>
              <Box
                component="input"
                inputMode="decimal"
                placeholder="0"
                value={formatNumericInput(amountStr)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // Strip the display commas, keep digits + one dot.
                  const v = e.target.value.replace(/,/g, "");
                  if (/^\d*\.?\d*$/.test(v)) setAmountStr(v);
                }}
                autoFocus
                sx={(t) => ({
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 40,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  color: t.palette.success.main,
                  "&::placeholder": { color: t.palette.text.disabled }
                })}
              />
            </Box>
            <Divider />

            {/* Sell: available-to-sell row */}
            {!isBuy && (
              <TradeRow
                label="Available to sell"
                value={formatUSD(available)}
              />
            )}

            {/* Buy: account + buying power rows */}
            {isBuy && (
              <>
                <TradeRow label="Account" value={accountName} />
                <TradeRow
                  label="Buying power"
                  value={formatUSD(bot.buyingPower)}
                />
              </>
            )}

            {/* Sell ≥ 90% — recommend liquidation */}
            {isLiquidation && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={(t) => ({
                  mt: 2,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(t.palette.info.main, 0.1)
                })}
              >
                <InfoOutlined
                  sx={{ fontSize: 18, color: "info.main", mt: 0.1 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  This sells 90%+ of the position — we recommend
                  liquidating the bot fully so no dust is left behind.
                </Typography>
              </Stack>
            )}

            {/* Buy: order-amount disclaimer */}
            {isBuy && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 2, color: "text.secondary" }}
              >
                <strong>Order amounts vary.</strong> Actual order amounts
                may deviate slightly from your input as the bot rebalances
                proportionally across its holdings.
              </Typography>
            )}
          </>
        ) : (
          /* ---- Confirm step ---- */
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={(t) => ({
                mb: 2,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: alpha(
                  (isBuy ? t.palette.success : t.palette.error).main,
                  0.1
                )
              })}
            >
              <BoltRounded
                sx={{
                  fontSize: 20,
                  color: isBuy ? "success.main" : "error.main"
                }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {isBuy
                  ? "Review your buy before it's submitted."
                  : isLiquidation
                    ? "Review the liquidation before it's submitted."
                    : "Review your sell before it's submitted."}
              </Typography>
            </Stack>

            <Divider />
            <TradeRow
              label="Amount"
              value={`${isBuy ? "" : "−"}${formatUSD(amount)}`}
              valueColor={isBuy ? "success.main" : "error.main"}
            />
            <TradeRow label="Account" value={accountName} />

            {/* Trades at — when the order is sent to market. */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Trades at
              </Typography>
              <TextField
                select
                variant="standard"
                value={timing}
                onChange={(e) => setTiming(e.target.value as TradeTiming)}
                InputProps={{ disableUnderline: true }}
                SelectProps={{
                  MenuProps: {
                    sx: (th: Theme) => ({ zIndex: th.zIndex.modal + 10 }),
                    PaperProps: { sx: { maxHeight: 320 } }
                  }
                }}
                sx={{
                  "& .MuiSelect-select": {
                    fontWeight: 700,
                    py: 0,
                    color: "text.primary",
                    textAlign: "right"
                  }
                }}
              >
                {TRADE_TIMING_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Divider />
          </>
        )}
      </Box>

      {/* Pinned footer — CTAs stay above the mobile keyboard. */}
      <Divider />
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        {step === "input" ? (
          <>
            <Button
              fullWidth
              variant="contained"
              color={isLiquidation ? "error" : "primary"}
              disabled={!canProceed}
              onClick={() => setStep("confirm")}
              sx={{ py: 1.25, fontWeight: 700, borderRadius: 2 }}
            >
              {inputCta}
            </Button>
            {!isBuy && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => {
                  setAmountStr(String(Math.round(available * 100) / 100));
                  setStep("confirm");
                }}
                sx={{ mt: 1, py: 1.25, fontWeight: 700, borderRadius: 2 }}
              >
                Sell all
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              fullWidth
              variant="contained"
              color={isLiquidation ? "error" : "primary"}
              onClick={handleConfirm}
              sx={{ py: 1.25, fontWeight: 700, borderRadius: 2 }}
            >
              Confirm
            </Button>
            <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => setStep("input")}
              sx={{ mt: 0.5, py: 1, fontWeight: 700 }}
            >
              Modify
            </Button>
          </>
        )}
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Internals ------------------------------------------------------------

function TradeRow({
  label,
  value,
  valueColor
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 1.25 }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: valueColor ?? "text.primary",
            fontFeatureSettings: '"tnum" 1'
          }}
        >
          {value}
        </Typography>
      </Stack>
      <Divider />
    </>
  );
}
